import React from 'react';
import { ShieldCheck, Zap, BarChart3, ArrowLeft, ExternalLink, Code, Rocket, Users, Lightbulb, Target } from 'lucide-react';

interface ConsultingSectionProps {
  onBack?: () => void;
}

const ConsultingSection: React.FC<ConsultingSectionProps> = ({ onBack }) => {
  const CALENDLY_LINK = "https://calendly.com/lab_z/virtual-coffee-discovery-20-minutes";

  return (
    <section id="consulting" className="min-h-screen py-32 md:py-48 bg-cream text-charcoal px-6 relative overflow-hidden">
      <div className="max-w-6xl mx-auto relative z-10">
        {onBack && (
          <button 
            onClick={onBack}
            className="mb-12 flex items-center gap-2 text-neutral-400 hover:text-terracotta transition-colors group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em]">Return Home</span>
          </button>
        )}
        
        <div className="grid lg:grid-cols-2 gap-24 items-start">
          <div className="space-y-12">
            <div className="space-y-6">
              <span className="text-terracotta text-[10px] font-black uppercase tracking-[0.4em]">The Practice</span>
              <h2 className="text-6xl md:text-8xl leading-[0.85] tracking-tighter font-black text-charcoal">Strategic Advisory.</h2>
              <p className="text-neutral-800 text-xl md:text-2xl font-light leading-relaxed max-w-xl">
                Grounded strategy for organizations navigating the era of machine intelligence, powered by the innovation laboratory at <a href="https://labz.io" target="_blank" rel="noopener noreferrer" className="text-charcoal font-bold hover:text-terracotta transition-colors inline-flex items-center gap-1">Lab Z <ExternalLink size={14} /></a>.
              </p>
            </div>
            
            <div className="grid gap-10">
              <div className="flex gap-6 items-start border-t border-neutral-100 pt-10">
                <div className="mt-1 text-terracotta bg-white p-3 rounded-full shadow-sm"><ShieldCheck size={20} /></div>
                <div>
                  <h4 className="font-bold text-charcoal uppercase text-xs tracking-[0.2em] mb-2">Responsible Governance</h4>
                  <p className="text-base text-neutral-800 font-medium leading-relaxed">Developing ethical frameworks that align AI adoption with human-centric values and long-term organizational health.</p>
                </div>
              </div>
              <div className="flex gap-6 items-start border-t border-neutral-100 pt-10">
                <div className="mt-1 text-terracotta bg-white p-3 rounded-full shadow-sm"><Zap size={20} /></div>
                <div>
                  <h4 className="font-bold text-charcoal uppercase text-xs tracking-[0.2em] mb-2">Operational Literacy</h4>
                  <p className="text-base text-neutral-800 font-medium leading-relaxed">Empowering leadership teams and staff to move from AI-anxiety to creative mastery through bespoke workshops.</p>
                </div>
              </div>
              <div className="flex gap-6 items-start border-t border-neutral-100 pt-10">
                <div className="mt-1 text-terracotta bg-white p-3 rounded-full shadow-sm"><BarChart3 size={20} /></div>
                <div>
                  <h4 className="font-bold text-charcoal uppercase text-xs tracking-[0.2em] mb-2">Human-AI Strategy</h4>
                  <p className="text-base text-neutral-800 font-medium leading-relaxed">Designing systems and workflows that augment human intuition rather than replacing it.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-32 space-y-8">
            <div className="bg-[#1A1A1A] text-white p-10 md:p-12 rounded-2xl shadow-[0_40px_80px_-15px_rgba(0,0,0,0.5)] relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <Rocket size={120} />
              </div>
              
              <div className="relative z-10 space-y-8">
                <div>
                  <span className="text-terracotta text-[10px] font-black uppercase tracking-[0.4em]">One-Day Intensive</span>
                  <h3 className="text-3xl md:text-4xl font-black tracking-tight mt-2 italic text-white">The AI Jumpstart.</h3>
                  <p className="text-neutral-100 mt-4 text-lg font-medium leading-relaxed">
                    Designed for teams (4–50) who feel "lost" in the AI noise. A rigorous day of sensemaking and building.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="flex gap-5">
                    <div className="flex-none w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-[10px] font-black text-terracotta border border-white/10">01</div>
                    <div>
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-neutral-300 mb-1">Knowledge & Meaning Shifts</h4>
                      <p className="text-sm font-bold text-white">Exploring the fundamental shifts in how we define work and humanity.</p>
                    </div>
                  </div>
                  <div className="flex gap-5">
                    <div className="flex-none w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-[10px] font-black text-terracotta border border-white/10">02</div>
                    <div>
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-neutral-300 mb-1">Human Innovation Exercises</h4>
                      <p className="text-sm font-bold text-white">Facilitated exercises to pinpoint the specific organizational problems where AI can amplify your unique value.</p>
                    </div>
                  </div>
                </div>

                <div className="pt-8 border-t border-white/10">
                  <a 
                    href={CALENDLY_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center bg-white text-charcoal py-5 rounded-lg font-black uppercase tracking-[0.3em] text-[10px] hover:bg-terracotta hover:text-white transition-all shadow-xl"
                  >
                    Get Un-Lost: Book a Discovery
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 p-10 md:p-12 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-black tracking-tight mb-6 text-charcoal">Long-term Implementation</h3>
              <p className="text-neutral-800 text-base leading-relaxed mb-10 font-medium">
                Ongoing support in change management, ethical governance, and deep technical integration.
              </p>
              <div className="space-y-4">
                <a 
                  href={CALENDLY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-terracotta text-white py-5 rounded-lg font-black uppercase tracking-[0.3em] text-[10px] hover:bg-[#A85B4E] hover:scale-[1.02] transition-all"
                >
                  Inquire about Advisory
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-20 -right-20 text-[15rem] font-black text-neutral-900/[0.03] select-none pointer-events-none leading-none rotate-6">
        STRATEGY
      </div>
    </section>
  );
};

export default ConsultingSection;